import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../services/firebaseFunctions';
import type {
  ListFormVersionsDocsRequest,
  ListFormVersionsDocsResponse,
} from '#types';

type AggregateResult = { type: 'count' | 'sum' | 'avg'; value: number };

const inFlightByKey = new Map<string, Promise<AggregateResult | null>>();

/**
 * Runs a listFormVersionsDocs aggregation query (count/sum/avg) without fetching documents.
 * De-dupes concurrent identical requests in-memory.
 */
export async function listFormVersionsDocsAggregate(
  request: Omit<ListFormVersionsDocsRequest, 'aggregateOnly' | 'includeCounts'>
): Promise<AggregateResult | null> {
  const aggregate = request.aggregate;
  if (!aggregate) {
    throw new Error('aggregate is required');
  }

  const fullRequest: ListFormVersionsDocsRequest = {
    ...request,
    aggregateOnly: true,
    includeCounts: false,
    aggregate,
  };

  const cacheKey = JSON.stringify(fullRequest);
  const existing = inFlightByKey.get(cacheKey);
  if (existing) {
    return existing;
  }

  const promise = (async () => {
    const callable = httpsCallable<ListFormVersionsDocsRequest, ListFormVersionsDocsResponse>(
      functions,
      'listFormVersionsDocs'
    );

    const result = await callable(fullRequest);
    return result.data.aggregateResult ?? null;
  })();

  inFlightByKey.set(cacheKey, promise);

  try {
    return await promise;
  } finally {
    inFlightByKey.delete(cacheKey);
  }
}


